import axios from 'axios'
import nprogress from 'nprogress'

import { ENV } from 'utils/environment'
import auth from 'api/routes/auth'
import { getCachedUser } from 'helpers/cachedUser'
import { refreshSession, clearSession } from 'utils/session'
import dueDiligence from './routes/dueDiligence'
import demand from './routes/demand'
import mrls from './routes/mrl'
import supply from './routes/supply'
import masterData from './routes/masterData'
import tableauDashboard from './routes/tableauDashboard'
import security from './routes/security/index'
import cropCensus from './routes/cropCensus'
import agronomy from './routes/agronomy'
import growerEstimates from './routes/growerEstimates'
import yieldDistribution from './routes/yieldDistribution'

const FALLBACK_MESSAGE = 'Something went wrong. Please try again later.'
const UNAUTHORISED_STATUS_CODE = 401
const FORBIDDEN_STATUS_CODE = 403
const FORBIDDEN_MESSAGE = 'Unauthorized'
const REFRESH_TOKEN_EP = 'auth/refresh'

const instance = axios.create({
	baseURL: ENV.BASE_URL,
	headers: {
		'Content-Type': 'application/json'
	}
})

instance.interceptors.request.use(
	async (request) => {
		nprogress.start()

		// Set headers
		const user = getCachedUser()
		request.headers = {
			...request.headers,
			'Content-Type': 'application/json',
			Authorization: `Bearer ${user?.token}`
		}
		return request
	},
	(error) => {
		nprogress.done()
		return error
	}
)

instance.interceptors.response.use(
	(response) => {
		nprogress.done()
		return response.data
	},
	async ({ response, config }) => {
		if (
			response.status === UNAUTHORISED_STATUS_CODE &&
			config.url !== REFRESH_TOKEN_EP
		) {
			const originalRequest = config

			try {
				const newToken = await refreshSession()

				const updatedOriginalReq = {
					...originalRequest,
					headers: {
						...originalRequest.headers,
						Authorization: `Bearer ${newToken}`
					}
				}
				return Promise.resolve(await (await axios(updatedOriginalReq)).data)
			} catch (error) {
				clearSession()
				return Promise.reject('Failed to refresh your session')
			}
		}

		nprogress.done()
		return Promise.reject(
			(response && response.data?.meta?.message) ||
				(response.status === FORBIDDEN_STATUS_CODE
					? FORBIDDEN_MESSAGE
					: FALLBACK_MESSAGE)
		)
	}
)

export default {
	auth: auth(instance),
	dueDiligence: dueDiligence(instance),
	demand: demand(instance),
	mrls: mrls(instance),
	supply: supply(instance),
	masterData: masterData(instance),
	tableauDashboard: tableauDashboard(instance),
	security: security(instance),
	cropCensus: cropCensus(instance),
	agronomy: agronomy(instance),
	growerEstimates: growerEstimates(instance),
	yieldDistribution: yieldDistribution(instance)
}
